import React, { useMemo, useState } from 'react';

const IRREGULAR_OPTIONS = [
  { value: 'work', label: 'WORK', helper: 'Employment, job offer, or self-employment' },
  { value: 'family', label: 'FAMILY', helper: 'Lives with family unit' },
  { value: 'vulnerability', label: 'VULNERABILITY', helper: 'Social-services vulnerability report' },
];

export default function CaseTypeEditor({ initialPathway, onSave, onClose, saving = false }) {
  const initialCategory = initialPathway === 'asylum'
    ? 'asylum'
    : (['work', 'family', 'vulnerability'].includes(initialPathway) ? 'irregular' : '');
  const [pathwayCategory, setPathwayCategory] = useState(initialCategory);
  const [irregularPathway, setIrregularPathway] = useState(
    ['work', 'family', 'vulnerability'].includes(initialPathway) ? initialPathway : '',
  );

  const pathway = useMemo(() => {
    if (pathwayCategory === 'asylum') return 'asylum';
    if (pathwayCategory === 'irregular') return irregularPathway;
    return '';
  }, [pathwayCategory, irregularPathway]);

  const isValid = Boolean(pathway);
  const isDirty = pathway !== (initialPathway || '');
  const canSave = isValid && isDirty && !saving;

  const selectedCode = useMemo(() => {
    if (pathway === 'asylum') return 'R31';
    if (pathway === 'work') return 'R32W';
    if (pathway === 'family') return 'R32F';
    if (pathway === 'vulnerability') return 'R32V';
    return '';
  }, [pathway]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/20 p-3 sm:items-center sm:p-4">
      <div className="w-full max-w-[480px] max-h-[calc(100dvh-1.5rem)] overflow-y-auto rounded-xl border border-slate-200 bg-white p-4 sm:p-6">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-lg">
            <i className="ri-edit-box-line text-slate-600 text-base" />
          </div>
          <h3 className="text-sm font-semibold text-slate-800">Edit Pathway</h3>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-wide font-medium text-slate-500 mb-1 block">Pathway</label>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => { setPathwayCategory('asylum'); setIrregularPathway(''); }}
                disabled={saving}
                className={`w-full text-left px-4 py-2.5 rounded-xl border text-xs transition-all ${
                  pathwayCategory === 'asylum'
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                    : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300 cursor-pointer'
                } ${saving ? 'cursor-not-allowed opacity-70' : ''}`}
              >
                <p className="font-semibold">ASYLUM</p>
                <p className="text-[10px] text-slate-400 mt-0.5">Filed for international protection before 1 Jan 2026</p>
              </button>
              <button
                type="button"
                onClick={() => setPathwayCategory('irregular')}
                disabled={saving}
                className={`w-full text-left px-4 py-2.5 rounded-xl border text-xs transition-all ${
                  pathwayCategory === 'irregular'
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                    : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300 cursor-pointer'
                } ${saving ? 'cursor-not-allowed opacity-70' : ''}`}
              >
                <p className="font-semibold">IRREGULAR SITUATION</p>
                <p className="text-[10px] text-slate-400 mt-0.5">Select one route: Work, Family, or Vulnerability</p>
              </button>
            </div>
          </div>

          {pathwayCategory === 'irregular' ? (
            <div>
              <label className="text-[11px] uppercase tracking-wide font-medium text-slate-500 mb-1 block">Irregular Route</label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {IRREGULAR_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setIrregularPathway(opt.value)}
                    disabled={saving}
                    className={`min-h-[44px] text-center px-3 py-2 rounded-lg border text-[11px] font-semibold transition-all ${
                      irregularPathway === opt.value
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 cursor-pointer'
                    } ${saving ? 'cursor-not-allowed opacity-70' : ''}`}
                    title={opt.helper}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="flex flex-col gap-2 pt-2 min-[420px]:flex-row min-[420px]:gap-3">
            <button
              type="button"
              onClick={() => onSave(pathway, selectedCode)}
              disabled={!canSave}
              className={`min-h-[44px] flex-1 text-xs font-semibold py-2 rounded-lg transition-colors whitespace-nowrap ${
                canSave
                  ? 'bg-slate-900 text-white hover:bg-slate-800 cursor-pointer'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              }`}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="min-h-[44px] flex-1 text-xs font-medium py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

